import React, { createContext, ReactNode, useEffect, useState, useContext, useCallback, useRef } from 'react';
import { NewsCluster, ContrastingView as TypesContrastingView, WikiConcept, TimelineEvent } from '../types/types';
import { api } from '../api/apiService';
import { AnalyticProfileContext, AnalyticProfileContextType } from './AnalyticProfileContext';
import { UserContext } from './UserContext';
import {
  isDefaultPortalPrimaryTitle,
  tryParsePortalDraft,
} from '../portal/portalDraftEnvelope';
import {
  clearNotebookDraftLifetimeSave,
  markNotebookDraftLifetimeSave,
} from '../utils/notebookDraftLifetimeSave';

// Define the Notebook type to match the backend
export interface Notebook {
  id: string;
  title: string;
  description: string;
  clusters: NewsCluster[];
  draft?: DraftContent;
  visibility: 'public' | 'private' | 'restricted';
  allowed_profiles: string[];
  /** Other portal notebooks linked to this draft (hex ids). */
  linked_notebook_ids?: string[];
  owner_profile_id: string;
  status: 'active' | 'archived' | 'trash';
  created_at: number;
  updated_at: number;
}

// Social Media Post type
export interface SocialMediaPost {
  post_number: number;
  platform: string;
  post_content: string;
  hashtags: string[];
  post_type: string;
}

// Contrasting View type
export interface ContrastingView {
  point_of_view_type: string;
  point_of_view_description: string;
}

// Article Image type for storing selected images
export interface ArticleImage {
  id: string;
  source: string;
  url: string;
  width: number;
  height: number;
  tags: string[];
  author: string;
  author_url: string;
  license: string;
  keywords: string;
  selected_at: number;
  /** S3 object key (for AI-generated and persisted stock images) */
  s3_key?: string;
}

// Draft Content type
export interface DraftContent {
  title: string;
  content: string;
  social_media_posts?: SocialMediaPost[];
  contrasting_views?: ContrastingView[];
  suggested_titles?: string[];
  last_generated_at?: number;
  created_at?: number;
  updated_at?: number;
  ready_for_publishing?: boolean;
  publication_status?: 'ready_to_publish' | 'scheduled' | 'published';
  scheduled_publish_at?: number;
  published_at?: number;
  /** Article images attached for publishing */
  images?: ArticleImage[];
  /** User-picked wiki entities (from linked library) beyond cluster-derived mentions. */
  linked_wiki_entities?: WikiConcept[];
}

// Cluster content generation job status
export type ClusterContentJobStatus = 'processing' | 'completed' | 'failed';

// Cluster content generation result from the backend
export interface ClusterContentGenerationResult {
  article_text: string;
  suggested_titles: string[];
  social_media: SocialMediaPost[];
  contrasting_views: ContrastingView[];
  wiki_concepts: WikiConcept[];
  timeline: TimelineEvent[];
}

// Cluster content generation job
export interface ClusterContentJob {
  job_id: string;
  status: ClusterContentJobStatus;
  created_at: string;
  completed_at?: string;
  notebook_id: string;
  cluster_title: string;
  error?: string;
  result?: ClusterContentGenerationResult;
}

// Frontend representation of a notebook
export interface FrontendNotebook {
  id: string;
  name: string;
  description: string;
  clusters: NewsCluster[];
  cluster_ids: string[]; // Add this to keep track of cluster IDs without loading the full clusters
  draft?: DraftContent; // Add draft field
  created_at: string;
  updated_at: string;
  status: 'active' | 'archived' | 'trash';
  visibility: 'public' | 'private' | 'restricted';
  /** Analytic profile ids when visibility is `restricted`. */
  allowed_profiles?: string[];
  /** Other active portal notebooks bound to this draft (hex ids). */
  linked_notebook_ids?: string[];
  owner_profile_id?: string;
  clustersLoaded: boolean; // Add this to track if clusters have been loaded
}

// Define the context value type
type NotebooksContextType = {
  notebooks: FrontendNotebook[];
  isLoading: boolean;
  /** True once a fetch has succeeded; lets deep links tell "not loaded (yet)" from "missing". */
  hasLoaded: boolean;
  error: string | null;
  clusterContentJobs: Map<string, ClusterContentJob>; // Track cluster content generation jobs
  fetchNotebooks: () => Promise<boolean>;
  addNotebook: (notebook: Omit<FrontendNotebook, 'id' | 'created_at' | 'updated_at' | 'clustersLoaded' | 'cluster_ids'>) => Promise<FrontendNotebook | null>;
  updateNotebook: (id: string, updates: Partial<FrontendNotebook>) => Promise<boolean>;
  deleteNotebook: (id: string) => Promise<boolean>;
  changeNotebookStatus: (id: string, status: 'active' | 'archived' | 'trash') => Promise<boolean>;
  addClustersToNotebook: (notebookId: string, clusters: NewsCluster[]) => Promise<boolean>;
  removeClustersFromNotebook: (notebookId: string, clusterIds: string[]) => Promise<boolean>;
  getActiveNotebooks: () => FrontendNotebook[];
  updateDraftContent: (notebookId: string, title: string, content: string) => Promise<FrontendNotebook | null>;
  markDraftReadyForPublishing: (notebookId: string, ready: boolean) => Promise<boolean>;
  schedulePublication: (notebookId: string, scheduledDate: string, scheduledTime: string) => Promise<boolean>;
  generateDraftContent: (notebookId: string, draftContent: string) => Promise<{
    social_media_posts: SocialMediaPost[];
    contrasting_views: ContrastingView[];
    suggested_titles: string[];
    last_generated_at: number;
  } | null>;
  generateClusterContent: (notebookId: string, cluster: NewsCluster, language?: string) => Promise<string | null>;
  getClusterContentJobStatus: (jobId: string) => ClusterContentJob | undefined;
  isClusterContentGenerating: (clusterTitle: string, notebookId?: string) => boolean;
};

// Create the context with a default value
export const NotebooksContext = createContext<NotebooksContextType>({
  notebooks: [],
  isLoading: false,
  hasLoaded: false,
  error: null,
  clusterContentJobs: new Map(),
  fetchNotebooks: async () => false,
  addNotebook: async () => null,
  updateNotebook: async () => false,
  deleteNotebook: async () => false,
  changeNotebookStatus: async () => false,
  addClustersToNotebook: async () => false,
  removeClustersFromNotebook: async () => false,
  getActiveNotebooks: () => [],
  updateDraftContent: async () => null,
  markDraftReadyForPublishing: async () => false,
  schedulePublication: async () => false,
  generateDraftContent: async () => null,
  generateClusterContent: async () => null,
  getClusterContentJobStatus: () => undefined,
  isClusterContentGenerating: () => false,
});

// Define the props for the NotebooksProvider
type NotebooksProviderProps = {
  children: ReactNode;
};

function normalizeLinkedNotebookIds(raw: unknown): string[] | undefined {
  if (!Array.isArray(raw) || raw.length === 0) return undefined;
  const out = raw
    .map((x) => (typeof x === 'string' ? x.trim() : String(x)))
    .filter(Boolean);
  return out.length ? out : undefined;
}

// Helper function to convert backend notebook to frontend format
const convertToFrontendNotebook = (notebook: Notebook, clusters: NewsCluster[] = []): FrontendNotebook => {
  const sourceClusters = notebook.clusters || clusters;
  const nbExt = notebook as Notebook & { linked_notebook_ids?: unknown };
  return {
    id: notebook.id,
    name: notebook.title,
    description: notebook.description,
    clusters: sourceClusters,
    cluster_ids: sourceClusters.map(cluster => cluster.cluster_id || cluster.title),
    draft: notebook.draft,
    created_at: new Date(notebook.created_at * 1000).toISOString(),
    updated_at: new Date(notebook.updated_at * 1000).toISOString(),
    status: notebook.status as 'active' | 'archived' | 'trash',
    visibility: notebook.visibility as 'public' | 'private' | 'restricted',
    allowed_profiles: notebook.allowed_profiles || [],
    linked_notebook_ids: normalizeLinkedNotebookIds(nbExt.linked_notebook_ids),
    owner_profile_id: notebook.owner_profile_id,
    clustersLoaded: true,
  };
};

// Helper function to convert frontend notebook to backend format
const convertToBackendNotebook = (notebook: Partial<FrontendNotebook>, clustersToSave?: NewsCluster[]): Partial<Notebook> => {
  const backendNotebook: Partial<Notebook> = {
    title: notebook.name,
    description: notebook.description,
    visibility: notebook.visibility,
    status: notebook.status,
  };

  if (notebook.allowed_profiles !== undefined) {
    backendNotebook.allowed_profiles = notebook.allowed_profiles;
  }

  if (notebook.linked_notebook_ids !== undefined) {
    backendNotebook.linked_notebook_ids = notebook.linked_notebook_ids;
  }

  if (clustersToSave) {
    backendNotebook.clusters = clustersToSave;
  }

  // Include draft if provided (e.g., when persisting images)
  if (notebook.draft) {
    backendNotebook.draft = notebook.draft;
  }

  return backendNotebook;
};

const toClusterRefId = (cluster: NewsCluster): string => (cluster.cluster_id || '').trim();
const uniq = (ids: string[]): string[] => Array.from(new Set(ids.filter(Boolean)));

export const NotebooksProvider: React.FC<NotebooksProviderProps> = ({ children }) => {
  const { selectedProfileId, selectedProfile } = useContext<AnalyticProfileContextType>(AnalyticProfileContext);
  const { user } = useContext(UserContext);
  // Users without notebook access (e.g. Social Listening only) would just get 403s.
  const canReadNotebooks = Boolean(user?.permissions?.notebooks?.read);
  const [notebooks, setNotebooks] = useState<FrontendNotebook[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasLoaded, setHasLoaded] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [clusterContentJobs, setClusterContentJobs] = useState<Map<string, ClusterContentJob>>(new Map());
  
  // Keep track of active polling intervals
  const pollingIntervalsRef = useRef<Map<string, NodeJS.Timeout>>(new Map());

  // Fetch notebooks from backend API (stable ref so consumers can safely depend on it in effects)
  const fetchNotebooks = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await api.get('/api/notebooks');
      
      if (response && Array.isArray(response)) {
        // Convert notebooks to frontend format with stored cluster data
        const frontendNotebooks = response.map((notebook: Notebook) => {
          return convertToFrontendNotebook(notebook);
        });

        setNotebooks(frontendNotebooks);
      }
      setHasLoaded(true);
      return true;
    } catch (error: any) {
      console.error('Error fetching notebooks:', error);
      setError(error.message || 'Failed to load notebooks');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Fetch notebooks whenever the profile changes
  useEffect(() => {
    console.log(`AnalyticProfile changed to: ${selectedProfileId}`);
    
    // Fetch notebooks whenever the profile changes
    if (selectedProfileId && canReadNotebooks) {
      console.log(`Fetching notebooks for profile: ${selectedProfileId}`);
      void fetchNotebooks();
    }
  }, [selectedProfileId, canReadNotebooks, fetchNotebooks]);
  
  // Add a separate useEffect just to debug the context connection
  useEffect(() => {
    console.log("NotebooksProvider initialized with selectedProfileId:", selectedProfileId);
  }, [selectedProfileId]);

  // Helper function to check if a cluster needs content generation
  const clusterNeedsContentGeneration = (cluster: NewsCluster): boolean => {
    // A cluster needs content generation if it doesn't have article_text, social_media, etc.
    const hasArticleText = cluster.article_text && cluster.article_text.trim().length > 0;
    const hasSocialMedia = cluster.social_media && cluster.social_media.length > 0;
    const hasContrastingViews = cluster.contrasting_views && cluster.contrasting_views.length > 0;
    const hasTimeline = cluster.timeline && cluster.timeline.length > 0;
    const hasWikiConcepts = cluster.wiki_concepts && cluster.wiki_concepts.length > 0;
    
    // If it has any of the generated content, don't regenerate
    return !(hasArticleText || hasSocialMedia || hasContrastingViews || hasTimeline || hasWikiConcepts);
  };

  // Add a new notebook
  const addNotebook = async (notebookData: Omit<FrontendNotebook, 'id' | 'created_at' | 'updated_at' | 'clustersLoaded' | 'cluster_ids'>) => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Get the clusters from the input data
      const clustersToSave = notebookData.clusters || [];
      
      // Create a backend notebook object
      const backendNotebook = convertToBackendNotebook(notebookData, clustersToSave);
      
      // Contract: persist cluster references only.
      const clusterIdsToSave = uniq(clustersToSave.map(toClusterRefId));
      if (clustersToSave.length > 0 && clusterIdsToSave.length !== clustersToSave.length) {
        throw new Error('Some selected clusters are missing cluster_id and cannot be saved by reference.');
      }
      if (Object.prototype.hasOwnProperty.call(backendNotebook, 'clusters')) {
        delete (backendNotebook as { clusters?: unknown }).clusters;
      }
      
      // Send the request to create the notebook with cluster IDs
      const response = await api.post('/api/notebooks', {
        ...backendNotebook,
        cluster_ids: clusterIdsToSave,
      });
      const createdPayload =
        (response as { notebook?: unknown; data?: unknown } | null)?.notebook ||
        (response as { notebook?: unknown; data?: unknown } | null)?.data ||
        response;

      let notebookId =
        (createdPayload as { id?: string; _id?: string; notebook_id?: string } | null)?.id ||
        (createdPayload as { id?: string; _id?: string; notebook_id?: string } | null)?._id ||
        (createdPayload as { id?: string; _id?: string; notebook_id?: string } | null)?.notebook_id;

      // Some backends return a success envelope without id; recover by reloading notebooks.
      if (!notebookId) {
        const latest = await api.get('/api/notebooks');
        if (Array.isArray(latest)) {
          const byTitle = latest
            .filter((nb: Notebook) => (nb.title || '').trim() === (notebookData.name || '').trim())
            .sort((a: Notebook, b: Notebook) => (b.created_at || 0) - (a.created_at || 0));
          if (byTitle.length > 0) {
            const recovered = byTitle[0];
            notebookId = recovered.id;
          }
        }
      }

      if (response && notebookId) {
        const createdNotebookId = notebookId;
        
        // Format clusters before saving to state
        const processedClusters = clustersToSave.map(cluster => ({
          ...cluster,
          // Ensure all array properties exist
          articles: cluster.articles || [],
          suggested_titles: cluster.suggested_titles || [],
          social_media: cluster.social_media || [],
          contrasting_views: cluster.contrasting_views || [],
          wiki_concepts: cluster.wiki_concepts || [],
          timeline: cluster.timeline || []
        }));
        
        const clusterIds = uniq(processedClusters.map(toClusterRefId));
        
        // Create the frontend representation with the returned ID
        const newNotebook: FrontendNotebook = {
          id: createdNotebookId,
          name: notebookData.name,
          description: notebookData.description || '',
          clusters: processedClusters, // Include processed clusters
          cluster_ids: clusterIds,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          status: notebookData.status || 'active',
          visibility: notebookData.visibility || 'private',
          draft: notebookData.draft,
          allowed_profiles: notebookData.allowed_profiles || [],
          owner_profile_id:
            (createdPayload as Notebook | null)?.owner_profile_id ||
            (response as Notebook | null)?.owner_profile_id,
          clustersLoaded: true, // Mark as loaded since we already have the clusters
        };
        
        // Update the notebooks state
        setNotebooks(prevNotebooks => [...prevNotebooks, newNotebook]);

        // Trigger cluster content generation for each cluster that needs it
        // Do this after the notebook is created and state is updated
        for (const cluster of processedClusters) {
          if (clusterNeedsContentGeneration(cluster) && cluster.articles && cluster.articles.length > 0) {
            console.log(`Triggering content generation for cluster: ${cluster.title}`);
            // Use setTimeout to avoid blocking and allow state updates
            // Use the selected profile's language, default to English
            const language = selectedProfile?.selected_language || 'English';
            setTimeout(() => {
              generateClusterContentInternal(createdNotebookId, cluster, language);
            }, 100);
          }
        }

        return newNotebook;
      }
      throw new Error('Notebook was created but no notebook id was returned by the API response.');
    } catch (error: any) {
      console.error('Error creating notebook:', error);
      const backendMessage =
        error?.response?.data?.detail ||
        error?.response?.data?.message ||
        error?.response?.data?.error;
      setError(backendMessage || error.message || 'Failed to create notebook');
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  // Internal function to generate cluster content (doesn't set loading state)
  const generateClusterContentInternal = async (notebookId: string, cluster: NewsCluster, language: string = 'English'): Promise<string | null> => {
    try {
      // Extract article URLs from the cluster
      const articleURLs = cluster.articles?.map(article => article.url) || [];
      
      if (articleURLs.length === 0) {
        console.error('No articles in cluster to generate content from');
        return null;
      }

      console.log(`Starting cluster content generation for "${cluster.title}" with ${articleURLs.length} articles`);

      // Call the backend to start the generation job
      const response = await api.post(`/api/notebooks/${notebookId}/generate-cluster-content`, {
        cluster_id: cluster.cluster_id,
        cluster_title: cluster.title,
        article_urls: articleURLs,
        language: language,
      });

      if (response && response.job_id) {
        const jobId = response.job_id;
        
        // Add job to state
        const newJob: ClusterContentJob = {
          job_id: jobId,
          status: response.status || 'processing',
          created_at: new Date().toISOString(),
          notebook_id: notebookId,
          cluster_title: cluster.title,
        };
        
        setClusterContentJobs(prev => {
          const newMap = new Map(prev);
          newMap.set(jobId, newJob);
          return newMap;
        });

        // Start polling for job status
        pollClusterContentJobStatus(jobId, notebookId, cluster.title);

        console.log(`Started cluster content generation job: ${jobId}`);
        return jobId;
      }

      return null;
    } catch (error: any) {
      console.error('Error starting cluster content generation:', error);
      return null;
    }
  };

  // Update a notebook
  const updateNotebook = async (id: string, updates: Partial<FrontendNotebook>) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const backendUpdates = convertToBackendNotebook(updates);
      
      await api.put(`/api/notebooks/${id}`, backendUpdates);
      
      // Update local state
      setNotebooks(prevNotebooks => 
        prevNotebooks.map(notebook => 
          notebook.id === id 
            ? { 
                ...notebook, 
                ...updates, 
                updated_at: new Date().toISOString() 
              } 
            : notebook
        )
      );

      markNotebookDraftLifetimeSave(id);
      
      return true;
    } catch (error: any) {
      console.error('Error updating notebook:', error);
      setError(error.message || 'Failed to update notebook');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // Delete a notebook (permanent)
  const deleteNotebook = async (id: string) => {
    setIsLoading(true);
    setError(null);
    
    try {
      await api.delete(`/api/notebooks/${id}`);

      clearNotebookDraftLifetimeSave(id);
      
      // Remove from local state
      setNotebooks(prevNotebooks => 
        prevNotebooks.filter(notebook => notebook.id !== id)
      );
      
      return true;
    } catch (error: any) {
      console.error('Error deleting notebook:', error);
      setError(error.message || 'Failed to delete notebook');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // Change notebook status (active/archived/trash)
  const changeNotebookStatus = async (id: string, status: 'active' | 'archived' | 'trash') => {
    return updateNotebook(id, { status });
  };

  // Add clusters to a notebook
  const addClustersToNotebook = async (notebookId: string, clustersToAdd: NewsCluster[]) => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Find the notebook in local state first.
      let notebook = notebooks.find(n => n.id === notebookId);

      // If notebook isn't in local state yet (common right after creation),
      // query backend directly instead of relying on asynchronous state updates.
      if (!notebook) {
        const latest = await api.get('/api/notebooks');
        if (Array.isArray(latest)) {
          const backendNotebook = latest.find((nb: Notebook) => nb.id === notebookId);
          if (backendNotebook) {
            notebook = convertToFrontendNotebook(backendNotebook);
          }
        }
      }

      if (!notebook) {
        throw new Error('Notebook not found');
      }
      
      // Get current clusters
      const currentClusters = notebook.clusters || [];
      
      // Filter out duplicate clusters
      const existingClusterTitles = currentClusters.map(c => c.title);
      
      // Process new clusters to add
      const processedClusters = clustersToAdd
        .filter(c => !existingClusterTitles.includes(c.title))
        .map(cluster => ({
          ...cluster,
          // Ensure all array properties exist
          articles: cluster.articles || [],
          suggested_titles: cluster.suggested_titles || [],
          social_media: cluster.social_media || [],
          contrasting_views: cluster.contrasting_views || [],
          wiki_concepts: cluster.wiki_concepts || [],
          timeline: cluster.timeline || []
        }));
      
      // If no new clusters to add after filtering duplicates, return success
      if (processedClusters.length === 0) {
        return true;
      }
      
      // Combine existing and new clusters
      const updatedClusters = [...currentClusters, ...processedClusters];
      const existingClusterIds = notebook.cluster_ids || [];
      const incomingClusterIds = processedClusters.map(toClusterRefId);
      if (processedClusters.length > 0 && incomingClusterIds.some((id) => !id)) {
        throw new Error('Some selected clusters are missing cluster_id and cannot be added to notebook.');
      }
      const updatedClusterIds = uniq([...existingClusterIds, ...incomingClusterIds]);

      await api.put(`/api/notebooks/${notebookId}`, { cluster_ids: updatedClusterIds });
      
      // Update local state
      setNotebooks(prevNotebooks => 
        prevNotebooks.map(nb => {
          if (nb.id === notebookId) {
            return {
              ...nb,
              clusters: updatedClusters,
              cluster_ids: updatedClusterIds,
              updated_at: new Date().toISOString(),
              clustersLoaded: true
            };
          }
          return nb;
        })
      );

      // Trigger cluster content generation for each new cluster that needs it
      // Use the selected profile's language, default to English
      const language = selectedProfile?.selected_language || 'English';
      for (const cluster of processedClusters) {
        if (clusterNeedsContentGeneration(cluster) && cluster.articles && cluster.articles.length > 0) {
          console.log(`Triggering content generation for newly added cluster: ${cluster.title}`);
          setTimeout(() => {
            generateClusterContentInternal(notebookId, cluster, language);
          }, 100);
        }
      }
      
      return true;
    } catch (error: any) {
      console.error('Error adding clusters to notebook:', error);
      const backendMessage =
        error?.response?.data?.detail ||
        error?.response?.data?.message ||
        error?.response?.data?.error;
      setError(backendMessage || error.message || 'Failed to add clusters to notebook');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // Remove clusters from a notebook
  const removeClustersFromNotebook = async (notebookId: string, clusterIds: string[]) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const notebook = notebooks.find(n => n.id === notebookId);
      if (!notebook) {
        throw new Error('Notebook not found');
      }
      
      const idsToRemove = new Set(clusterIds.filter(Boolean));
      const updatedClusters = notebook.clusters.filter(
        (cluster) => !idsToRemove.has(cluster.cluster_id || '')
      );
      const updatedClusterIds = (notebook.cluster_ids || []).filter((id) => !idsToRemove.has(id));

      await api.put(`/api/notebooks/${notebookId}`, { cluster_ids: updatedClusterIds });
      
      // Update local state
      setNotebooks(prevNotebooks => 
        prevNotebooks.map(nb => {
          if (nb.id === notebookId) {
            return {
              ...nb,
              clusters: updatedClusters,
              cluster_ids: updatedClusterIds,
              updated_at: new Date().toISOString(),
              clustersLoaded: true
            };
          }
          return nb;
        })
      );
      
      return true;
    } catch (error: any) {
      console.error('Error removing clusters from notebook:', error);
      setError(error.message || 'Failed to remove clusters from notebook');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // Get only active notebooks
  const getActiveNotebooks = () => {
    return notebooks.filter(notebook => notebook.status === 'active');
  };

  // Update draft content
  const updateDraftContent = async (notebookId: string, title: string, content: string): Promise<FrontendNotebook | null> => {
    setIsLoading(true);
    setError(null);
    
    try {
      const notebook = notebooks.find(n => n.id === notebookId);
      if (!notebook) {
        throw new Error('Notebook not found');
      }

      const draftEditedAt = Date.now();
      // Update the draft content
      const updatedDraft: DraftContent = {
        ...(notebook.draft || { title: '', content: '' }),
        title: title,
        content: content,
        updated_at: draftEditedAt,
      };

      const t = title.trim();
      const parsed = tryParsePortalDraft((content || '').trim());
      const kind = parsed?.kind;
      const titleActsUnset = !t || (kind != null && isDefaultPortalPrimaryTitle(kind, t));
      const nextName = (titleActsUnset ? (notebook.name || '').trim() || t : t || (notebook.name || '').trim()).trim();

      const response = await api.put(`/api/notebooks/${notebookId}`, {
        draft: updatedDraft,
        title: nextName,
      });
      
      // The axios instance already extracts data, so response IS the data
      let updatedNotebook = convertToFrontendNotebook(response as unknown as Notebook, notebook.clusters);
      updatedNotebook = { ...updatedNotebook, name: nextName };
      updatedNotebook = {
        ...updatedNotebook,
        draft: updatedNotebook.draft
          ? { ...updatedNotebook.draft, updated_at: draftEditedAt }
          : { ...updatedDraft },
      };

      // Update local state with the actual timestamps from backend
      setNotebooks(prevNotebooks =>
        prevNotebooks.map(nb => (nb.id === notebookId ? updatedNotebook : nb))
      );

      return updatedNotebook;
    } catch (error: any) {
      console.error('Error updating draft content:', error);
      setError(error.message || 'Failed to update draft content');
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  // Mark draft as ready for publishing
  const markDraftReadyForPublishing = async (notebookId: string, ready: boolean) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const notebook = notebooks.find(n => n.id === notebookId);
      if (!notebook) {
        throw new Error('Notebook not found');
      }

      // One-way: once ready, do not clear via API (editing stays locked in UI).
      if (!ready && notebook.draft?.ready_for_publishing) {
        return true;
      }
      if (ready && notebook.draft?.ready_for_publishing) {
        return true;
      }

      const draftEditedAt = Date.now();
      // Update the ready_for_publishing flag
      const updatedDraft: DraftContent = {
        ...(notebook.draft || { title: '', content: '' }),
        ready_for_publishing: ready,
        publication_status: ready ? 'ready_to_publish' : undefined,
        updated_at: draftEditedAt,
      };

      await api.put(`/api/notebooks/${notebookId}`, { draft: updatedDraft });

      // Update local state
      const touchedAt = new Date().toISOString();
      setNotebooks(prevNotebooks =>
        prevNotebooks.map(nb => {
          if (nb.id !== notebookId) return nb;
          const baseDraft = nb.draft || { title: '', content: '' };
          return {
            ...nb,
            updated_at: touchedAt,
            draft: {
              ...baseDraft,
              ready_for_publishing: ready,
              publication_status: ready ? 'ready_to_publish' : undefined,
              updated_at: draftEditedAt,
            },
          };
        })
      );

      if (ready) {
        markNotebookDraftLifetimeSave(notebookId);
      }

      return true;
    } catch (error: any) {
      console.error('Error marking draft ready for publishing:', error);
      setError(error.message || 'Failed to mark draft ready');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // Schedule publication
  const schedulePublication = async (notebookId: string, scheduledDate: string, scheduledTime: string) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const notebook = notebooks.find(n => n.id === notebookId);
      if (!notebook) {
        throw new Error('Notebook not found');
      }

      // Convert date and time to Unix timestamp
      const dateTimeString = `${scheduledDate}T${scheduledTime}`;
      const scheduledTimestamp = Math.floor(new Date(dateTimeString).getTime() / 1000);

      const draftEditedAt = Date.now();
      // Update the publication status and scheduled time
      const updatedDraft: DraftContent = {
        ...(notebook.draft || { title: '', content: '' }),
        publication_status: 'scheduled',
        scheduled_publish_at: scheduledTimestamp,
        updated_at: draftEditedAt,
      };

      await api.put(`/api/notebooks/${notebookId}`, { draft: updatedDraft });

      // Update local state
      const touchedAt = new Date().toISOString();
      setNotebooks(prevNotebooks =>
        prevNotebooks.map(nb => {
          if (nb.id !== notebookId) return nb;
          const baseDraft = nb.draft || { title: '', content: '' };
          return {
            ...nb,
            updated_at: touchedAt,
            draft: {
              ...baseDraft,
              publication_status: 'scheduled',
              scheduled_publish_at: scheduledTimestamp,
              updated_at: draftEditedAt,
            },
          };
        })
      );

      return true;
    } catch (error: any) {
      console.error('Error scheduling publication:', error);
      setError(error.message || 'Failed to schedule publication');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // Generate draft content (social media, contrasting views, suggested titles)
  const generateDraftContent = async (notebookId: string, draftContent: string) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await api.post(`/api/notebooks/${notebookId}/generate-draft-content`, {
        draft_content: draftContent,
      });
      
      if (response) {
        const genDraftAt = Date.now();
        // Update local state with the generated content
        setNotebooks(prevNotebooks => 
          prevNotebooks.map(nb => {
            if (nb.id === notebookId) {
              return {
                ...nb,
                draft: {
                  ...(nb.draft || { title: '', content: '' }),
                  content: draftContent,
                  social_media_posts: response.social_media_posts,
                  contrasting_views: response.contrasting_views,
                  suggested_titles: response.suggested_titles,
                  last_generated_at: response.last_generated_at,
                  updated_at: genDraftAt,
                },
                updated_at: new Date().toISOString(),
              };
            }
            return nb;
          })
        );
        
        return {
          social_media_posts: response.social_media_posts,
          contrasting_views: response.contrasting_views,
          suggested_titles: response.suggested_titles,
          last_generated_at: response.last_generated_at,
        };
      }
      return null;
    } catch (error: any) {
      console.error('Error generating draft content:', error);
      setError(error.message || 'Failed to generate draft content');
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  // Poll for cluster content generation job status
  const pollClusterContentJobStatus = useCallback(async (jobId: string, notebookId: string, clusterTitle: string) => {
    const POLL_INTERVAL = 3000; // Poll every 3 seconds
    const MAX_POLL_TIME = 5 * 60 * 1000; // 5 minutes max
    const startTime = Date.now();

    const poll = async () => {
      try {
        // Check if we've exceeded the max poll time
        if (Date.now() - startTime > MAX_POLL_TIME) {
          console.error(`Job ${jobId} polling timed out`);
          setClusterContentJobs(prev => {
            const newMap = new Map(prev);
            const job = newMap.get(jobId);
            if (job) {
              newMap.set(jobId, { ...job, status: 'failed', error: 'Job timed out' });
            }
            return newMap;
          });
          
          // Clear the polling interval
          const intervalId = pollingIntervalsRef.current.get(jobId);
          if (intervalId) {
            clearInterval(intervalId);
            pollingIntervalsRef.current.delete(jobId);
          }

          // Clean up the timed-out job from state after a short delay
          setTimeout(() => {
            setClusterContentJobs(prev => {
              const newMap = new Map(prev);
              newMap.delete(jobId);
              return newMap;
            });
            console.log(`Cleaned up timed-out job ${jobId} from state`);
          }, 2000);
          return;
        }

        const response = await api.get(`/api/notebooks/jobs/${jobId}`);
        
        if (response) {
          const job: ClusterContentJob = {
            job_id: response.job_id,
            status: response.status,
            created_at: response.created_at,
            completed_at: response.completed_at,
            notebook_id: response.notebook_id,
            cluster_title: response.cluster_title,
            error: response.error,
            result: response.result,
          };

          // Update the job in state
          setClusterContentJobs(prev => {
            const newMap = new Map(prev);
            newMap.set(jobId, job);
            return newMap;
          });

            // If job completed or failed, stop polling
            if (job.status === 'completed' || job.status === 'failed') {
              console.log(`Job ${jobId} ${job.status}`);
              
              // Clear the polling interval
              const intervalId = pollingIntervalsRef.current.get(jobId);
              if (intervalId) {
                clearInterval(intervalId);
                pollingIntervalsRef.current.delete(jobId);
              }

              // If completed, update the cluster in the notebook
              if (job.status === 'completed' && job.result) {
                console.log(`Updating cluster "${clusterTitle}" with generated content`);
                
                setNotebooks(prevNotebooks => 
                  prevNotebooks.map(nb => {
                    if (nb.id === notebookId) {
                      const updatedClusters = nb.clusters.map(cluster => {
                        if (cluster.title === clusterTitle) {
                          // Convert contrasting views to match the types.ts interface
                          const convertedContrastingViews: TypesContrastingView[] = (job.result?.contrasting_views || []).map(cv => ({
                            point_of_view_type: cv.point_of_view_type as 'expressed' | 'suggested',
                            point_of_view_description: cv.point_of_view_description,
                          }));

                          return {
                            ...cluster,
                            article_text: job.result?.article_text || cluster.article_text,
                            suggested_titles: job.result?.suggested_titles || cluster.suggested_titles,
                            social_media: job.result?.social_media || cluster.social_media,
                            contrasting_views: convertedContrastingViews.length > 0 ? convertedContrastingViews : cluster.contrasting_views,
                            wiki_concepts: job.result?.wiki_concepts || cluster.wiki_concepts,
                            timeline: job.result?.timeline || cluster.timeline,
                          };
                        }
                        return cluster;
                      });
                      
                      return {
                        ...nb,
                        clusters: updatedClusters,
                        updated_at: new Date().toISOString(),
                      };
                    }
                    return nb;
                  })
                );
              }

              // Clean up the job from state after a short delay to allow UI updates
              setTimeout(() => {
                setClusterContentJobs(prev => {
                  const newMap = new Map(prev);
                  newMap.delete(jobId);
                  return newMap;
                });
                console.log(`Cleaned up job ${jobId} from state`);
              }, 2000); // 2 second delay to allow loading indicators to update
            }
        }
      } catch (error) {
        console.error(`Error polling job ${jobId}:`, error);
      }
    };

    // Start polling
    poll(); // Initial poll
    const intervalId = setInterval(poll, POLL_INTERVAL);
    pollingIntervalsRef.current.set(jobId, intervalId);
  }, []);

  // Generate cluster content (article draft, contrasting views, timeline, wiki, social media)
  const generateClusterContent = async (notebookId: string, cluster: NewsCluster, language: string = 'English'): Promise<string | null> => {
    try {
      // Extract article URLs from the cluster
      const articleURLs = cluster.articles?.map(article => article.url) || [];
      
      if (articleURLs.length === 0) {
        console.error('No articles in cluster to generate content from');
        setError('No articles in cluster to generate content from');
        return null;
      }

      console.log(`Starting cluster content generation for "${cluster.title}" with ${articleURLs.length} articles`);

      // Call the backend to start the generation job
      const response = await api.post(`/api/notebooks/${notebookId}/generate-cluster-content`, {
        cluster_title: cluster.title,
        article_urls: articleURLs,
        language: language,
      });

      if (response && response.job_id) {
        const jobId = response.job_id;
        
        // Add job to state
        const newJob: ClusterContentJob = {
          job_id: jobId,
          status: response.status || 'processing',
          created_at: new Date().toISOString(),
          notebook_id: notebookId,
          cluster_title: cluster.title,
        };
        
        setClusterContentJobs(prev => {
          const newMap = new Map(prev);
          newMap.set(jobId, newJob);
          return newMap;
        });

        // Start polling for job status
        pollClusterContentJobStatus(jobId, notebookId, cluster.title);

        console.log(`Started cluster content generation job: ${jobId}`);
        return jobId;
      }

      return null;
    } catch (error: any) {
      console.error('Error starting cluster content generation:', error);
      setError(error.message || 'Failed to start cluster content generation');
      return null;
    }
  };

  // Get cluster content job status
  const getClusterContentJobStatus = (jobId: string): ClusterContentJob | undefined => {
    return clusterContentJobs.get(jobId);
  };

  // Check if a cluster is currently generating content
  const isClusterContentGenerating = (clusterTitle: string, notebookId?: string): boolean => {
    const jobs = Array.from(clusterContentJobs.values());
    return jobs.some(job => {
      if (job.cluster_title === clusterTitle && job.status === 'processing') {
        // If notebookId is provided, also check it matches
        if (notebookId && job.notebook_id !== notebookId) {
          return false;
        }
        return true;
      }
      return false;
    });
  };

  // Cleanup polling intervals on unmount
  useEffect(() => {
    const intervalsRef = pollingIntervalsRef;
    return () => {
      intervalsRef.current.forEach((intervalId) => {
        clearInterval(intervalId);
      });
      intervalsRef.current.clear();
    };
  }, []);

  return (
    <NotebooksContext.Provider 
      value={{ 
        notebooks, 
        isLoading,
        hasLoaded,
        error,
        clusterContentJobs,
        fetchNotebooks,
        addNotebook, 
        updateNotebook, 
        deleteNotebook, 
        changeNotebookStatus,
        addClustersToNotebook,
        removeClustersFromNotebook,
        getActiveNotebooks,
        updateDraftContent,
        markDraftReadyForPublishing,
        schedulePublication,
        generateDraftContent,
        generateClusterContent,
        getClusterContentJobStatus,
        isClusterContentGenerating,
      }}
    >
      {children}
    </NotebooksContext.Provider>
  );
};

export default NotebooksProvider; 